import { useEffect, useState } from 'react';
import { Sparkles, User, Mic, Star, Crown, Award, Loader2, AlertCircle, Check, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAppStore } from '@/stores/useAppStore';
import type { VoiceCategory } from '@/types';

const CATEGORY_ICONS: Record<string, typeof Sparkles> = {
  generated: Sparkles,
  cloned: User,
  premade: Mic,
  professional: Star,
  famous: Crown,
  high_quality: Award,
};

const CATEGORY_LABELS: Record<string, string> = {
  generated: '生成',
  cloned: 'クローン',
  premade: 'プリセット',
  professional: 'プロ',
  famous: '有名人',
  high_quality: '高品質',
};

const CATEGORY_KEYS: VoiceCategory[] = [
  'generated',
  'cloned',
  'premade',
  'professional',
  'famous',
  'high_quality',
];

function CategoryIcon({ category }: { category?: VoiceCategory }) {
  if (!category) return null;
  const Icon = CATEGORY_ICONS[category];
  if (!Icon) return null;
  return <Icon size={14} className="text-warm-400 shrink-0" />;
}

function CategoryLabel({ category }: { category?: VoiceCategory }) {
  if (!category) return null;
  const label = CATEGORY_LABELS[category];
  if (!label) return null;
  return <span className="text-xs text-warm-400">{label}</span>;
}

export function VoiceSelector() {
  const {
    settings,
    voices,
    voicesLoading,
    voicesError,
    voicesHasMore,
    voicesCurrentPage,
    voicesPageTokens,
    voicesFetched,
    updateSettings,
    fetchVoices,
    fetchVoicesPage,
  } = useAppStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<VoiceCategory | null>(null);

  const hasApiKey = settings.elevenLabsApiKey.length > 0;

  // 入力が落ち着いてからAPI検索を投げる
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // 検索語・カテゴリはAPIパラメータとして渡し、条件が変わったら先頭ページから取り直す。
  // 同一条件での再実行はストア側が無視するため、再マウント時に二重取得しない。
  // voicesFetchedを依存に含めるのは、API Key変更でキャッシュが破棄されたときに再取得するため。
  useEffect(() => {
    if (hasApiKey) {
      fetchVoices({ search: debouncedQuery, category: selectedCategory });
    }
  }, [hasApiKey, debouncedQuery, selectedCategory, voicesFetched, fetchVoices]);

  if (!hasApiKey) {
    return (
      <div className="text-sm text-warm-400 py-2">
        API Keyを入力すると、Voice一覧が表示されます
      </div>
    );
  }

  // 一覧を一度も表示できていない場合のみ、エラー表示に全体を切り替える
  // （検索・ページ送り中の一時的な失敗では、検索欄や取得済みの一覧を隠さない）
  if (voicesError && !voicesFetched) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-red-500">
          <AlertCircle size={14} />
          <span>{voicesError}</span>
        </div>
        <button
          onClick={() => fetchVoices()}
          className="btn-secondary text-sm"
          disabled={voicesLoading}
        >
          再取得
        </button>
      </div>
    );
  }

  // 初回取得中のみ全体をローディング表示にする（検索中は検索欄を保つ）
  if (voicesLoading && !voicesFetched) {
    return (
      <div className="flex items-center gap-2 text-sm text-warm-400 py-2">
        <Loader2 size={14} className="animate-spin" />
        <span>Voice一覧を取得中...</span>
      </div>
    );
  }

  // 選択中Voiceの表示は設定に保存した名前を使う
  //（一覧は現在ページ分しか持たないため、別ページのVoice選択中でも名前を出せるように）
  const selectedVoiceName =
    settings.voiceName ?? voices.find((v) => v.voiceId === settings.voiceId)?.name;

  const hasPrev = voicesCurrentPage > 0;
  // hasMoreでも次ページトークンが無いとfetchVoicesPageは何もしないため、トークンの有無まで確認する
  const hasNext = voicesHasMore && Boolean(voicesPageTokens[voicesCurrentPage]);

  return (
    <div className="space-y-2">
      {/* 選択中のVoice表示。名前は設定に保存されており、表示中のページに依存しない。
          IDフォールバックは名前保存機能の導入前に選択された設定のためだけに残す
          （一覧に選択中Voiceが現れた時点で自動的に名前が保存される） */}
      {settings.voiceId && (
        <div className="flex items-center gap-2 px-3 py-2 bg-coral-50 border border-coral-200 rounded-lg text-sm">
          <Check size={14} className="text-coral-500 shrink-0" />
          {selectedVoiceName ? (
            <>
              <CategoryIcon category={settings.voiceCategory} />
              <span className="font-medium text-warm-800 truncate">{selectedVoiceName}</span>
            </>
          ) : (
            <span className="text-warm-600 truncate">選択中: {settings.voiceId}</span>
          )}
        </div>
      )}

      {/* ページ送り中の一時的なエラー表示 */}
      {voicesError && (
        <div className="flex items-center gap-2 text-sm text-red-500">
          <AlertCircle size={14} />
          <span>{voicesError}</span>
        </div>
      )}

      {/* 検索 */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input-base pr-8 py-2 text-sm"
          placeholder="Voice名・説明で検索"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-400 hover:text-warm-600 transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* カテゴリフィルタ */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-0.5">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`shrink-0 px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
            selectedCategory === null
              ? 'bg-coral-100 text-coral-700 border-coral-200'
              : 'bg-warm-50 text-warm-500 border-warm-200 hover:bg-warm-100'
          }`}
        >
          すべて
        </button>
        {CATEGORY_KEYS.map((key) => {
          const Icon = CATEGORY_ICONS[key];
          const isActive = selectedCategory === key;
          return (
            <button
              key={key}
              onClick={() => setSelectedCategory(isActive ? null : key)}
              className={`shrink-0 px-2.5 py-1 text-xs font-medium rounded-full border transition-colors flex items-center gap-1 ${
                isActive
                  ? 'bg-coral-100 text-coral-700 border-coral-200'
                  : 'bg-warm-50 text-warm-500 border-warm-200 hover:bg-warm-100'
              }`}
            >
              {Icon && <Icon size={12} />}
              {CATEGORY_LABELS[key]}
            </button>
          );
        })}
      </div>

      {/* Voice一覧 (ページネーション) */}
      <div className="max-h-80 overflow-y-auto border border-warm-200 rounded-lg divide-y divide-warm-100 relative">
        {voicesLoading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 rounded-lg">
            <Loader2 size={20} className="animate-spin text-coral-500" />
          </div>
        )}
        {voices.length === 0 && (searchQuery || selectedCategory) && (
          <p className="text-sm text-warm-400 text-center py-4">
            該当するVoiceが見つかりません
          </p>
        )}
        {voices.length === 0 && !searchQuery && !selectedCategory && (
          <p className="text-sm text-warm-400 text-center py-4">
            Voiceがありません
          </p>
        )}
        {voices.map((voice) => {
          const isSelected = voice.voiceId === settings.voiceId;
          return (
            <button
              key={voice.voiceId}
              onClick={() =>
                updateSettings({
                  voiceId: voice.voiceId,
                  voiceName: voice.name,
                  voiceCategory: voice.category,
                })
              }
              className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 transition-colors hover:bg-warm-50 ${
                isSelected ? 'bg-coral-50' : ''
              }`}
            >
              <CategoryIcon category={voice.category} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`text-sm truncate ${isSelected ? 'font-medium text-coral-700' : 'text-warm-700'}`}>
                    {voice.name}
                  </span>
                  <CategoryLabel category={voice.category} />
                </div>
                {voice.description && (
                  <p className="text-xs text-warm-400 mt-0.5 line-clamp-2">{voice.description}</p>
                )}
              </div>
              {isSelected && <Check size={14} className="text-coral-500 shrink-0 mt-0.5" />}
            </button>
          );
        })}
      </div>

      {/* ページネーション */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => fetchVoicesPage('prev')}
          disabled={!hasPrev || voicesLoading}
          className="btn-small"
        >
          <ChevronLeft size={14} />
          前へ
        </button>
        <span className="text-xs text-warm-400">
          ページ {voicesCurrentPage + 1}
        </span>
        <button
          onClick={() => fetchVoicesPage('next')}
          disabled={!hasNext || voicesLoading}
          className="btn-small"
        >
          次へ
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
